import {useCallback, useEffect, useState} from "react";
import {PricingApi} from "pricing/PricingApi";
import PricingStorage, {MONTHLY} from "pricing/PricingStorage";
import {BAKED_PRICES} from "pricing/bakedCatalogue";
import {PAID_PLAN_IDS, pricingCountryForContext} from "pricing/planCatalogue";
import {useLocale} from "i18n";
import {storefrontLocale} from "i18n/localePreference";
import {UpsellApi} from "./UpsellApi";
import {visibleFreshOfferSpotsLeft} from "./firstMonthOffer";
import {firstMonthMajor, firstMonthPlansFrom, type FirstMonthPlanRow} from "./firstMonthPricing";

/**
 * Every monthly tier, priced with and without the offer (kitecorp/transferify-ui#1448).
 *
 * <b>The discount is asked PER TIER rather than assumed uniform.</b> One coupon backs all of them
 * today, so the three answers are the same percentage — but `advertisablePercentOffFor` is a
 * per-price question on the api, and a coupon carrying `applies_to` would legitimately answer
 * differently for each. Asking once and multiplying the answer across three prices would be this
 * client re-implementing the offer, which is the failure `UpsellApi` exists to prevent; three asks,
 * behind a dialog the reader opened deliberately, is the cost of not doing that.
 *
 * <b>MONTHLY only, and not as a default.</b> The coupon is monthly-only and the api refuses to quote
 * a yearly price at all, so a yearly row could only ever be an undiscounted one presented inside an
 * offer — worse than not showing it.
 *
 * <b>A FAILED READ FALLS BACK TO THE BAKED CATALOGUE (kitecorp/transferify-ui#1671).</b> It used to
 * leave the list empty, and the dialog disables Continue on an empty list, so one failed `GET /plan`
 * stranded a reader who had just pressed Claim on all four surfaces that open it. The rows are then
 * priced from the amounts this bundle already carries, by the React-free rule in
 * `./firstMonthPricing`; the live read stays the PRIMARY source because it is the api's authoritative
 * quote and the only one that can report the campaign's remaining spots.
 *
 * @param active     false suppresses every ask; the dialog passes its own open state, so a closed
 *                   dialog costs nothing at all.
 * @param percentOff the percentage the api already quoted for this offer, carried in from the surface
 *                   whose claim button opened the dialog. Only the fallback reads it — a live read
 *                   asks per tier — and it is never a figure this client chose.
 */
export function useFirstMonthPlans(active: boolean, percentOff: number | null): {
	rows: FirstMonthPlanRow[];
	/**
	 * An attempt has finished. With {@link rows} this makes the caller's three states exact: not
	 * settled is "still asking", settled with rows is the offer, and settled with none is the failure.
	 *
	 * Stated as SETTLED rather than as its complement `loading`, because the two are not complements
	 * before the first attempt starts: the effect that begins it runs after the opening render, so a
	 * `loading` flag reads false for one frame with nothing to show, and a caller keying its failure
	 * message off `!loading` would flash that message every time the dialog opens.
	 */
	settled: boolean;
	spotsLeft: number | null;
	/** Ask again. The only recovery left once even the baked catalogue could price nothing. */
	retry: () => void;
} {
	const {locale} = useLocale();
	const [rows, setRows] = useState<FirstMonthPlanRow[]>([]);
	const [settled, setSettled] = useState(false);
	const [spotsLeft, setSpotsLeft] = useState<number | null>(null);
	// Bumped by `retry`, and a dependency of the effect below, so pressing Retry re-runs the whole
	// read rather than needing a second copy of it here.
	const [attempt, setAttempt] = useState(0);
	const retry = useCallback(() => setAttempt(previous => previous + 1), []);

	useEffect(() => {
		if (!active) {
			// The ROWS are deliberately left alone here. BaseDialog keeps its subtree mounted through the
			// close transition, so clearing them swapped the tier list for "Loading plans…" on the way out
			// — a dialog that appears to start working again as it disappears. They are cleared when the
			// next opening begins instead, which is where a stale list would actually mislead.
			setSettled(false);
			setSpotsLeft(null);
			return;
		}
		let disposed = false;
		setRows([]);
		setSettled(false);
		setSpotsLeft(null);
		void (async () => {
			let live: {rows: FirstMonthPlanRow[]; spotsLeft: number | null} | null = null;
			try {
				// Refetched rather than read from storage, for the same reason the checkout refetches:
				// locale/domain currency must never inherit a stale cache, and the price ids shown here
				// have to be the very ones the CTA would buy.
				const {products} = await PricingApi.getPlan(
					pricingCountryForContext(storefrontLocale(), window.location.hostname));
				PricingStorage.setProducts(products);
				const priced = PAID_PLAN_IDS.flatMap(plan => {
					const price = PricingStorage.priceFor(plan, MONTHLY);
					// A tier the catalogue has no monthly price for is DROPPED, not rendered at zero: an
					// incomplete catalogue is a wire problem, and inventing a price for it would put a
					// number on screen that no Stripe price backs.
					return price?.id ? [{plan, price}] : [];
				});
				const quoted = await Promise.all(priced.map(async ({plan, price}) => {
					// One tier failing must not empty the dialog: it renders at full price instead, which
					// is the understating direction the whole feature is biased towards.
					// An explicitly opened picker must not consume another automatic presentation.
					const offer = await UpsellApi.getOffer(price.id, false).catch(() => null);
					const quotedPercentOff = offer?.percentOff ?? null;
					return {
						offer,
						row: {
							plan,
							currency: price.currency,
							monthlyAmount: price.amount,
							firstMonthAmount: quotedPercentOff === null
								? null
								: firstMonthMajor(price.amount, price.currency, quotedPercentOff),
						},
					};
				}));
				live = {
					rows: quoted.map(({row}) => row),
					spotsLeft: visibleFreshOfferSpotsLeft(quoted.map(({offer}) => offer)),
				};
			} catch {
				// no-state: swallowed on purpose. `live` stays null, which is what the rule below reads as
				// a failed read, and the baked catalogue answers it. A reader who is shown prices does not
				// need to be told which source produced them.
			}
			// `finally`, not a plain tail: this hook's whole purpose is that the dialog never sits on a
			// dead Continue, and anything that threw before `setSettled` would produce exactly that — a
			// permanent "Loading plans…". Nothing inside is expected to throw (`firstMonthPlansFrom` is
			// total, deliberately); the point is that a later edit cannot make it strand the reader
			// without also failing loudly, which is the whole defect ui#1671 was filed about.
			try {
				// WHICH SOURCE WINS is not decided here. `firstMonthPlansFrom` is the React-free rule, so
				// all three of its branches are pinned by a test rather than only by the dialog rendering
				// them.
				const plans = firstMonthPlansFrom(
					live, BAKED_PRICES, storefrontLocale(), window.location.hostname, percentOff);
				if (!disposed) {
					setRows(plans.rows);
					setSpotsLeft(plans.spotsLeft);
				}
			} finally {
				if (!disposed) {
					setSettled(true);
				}
			}
		})();
		return () => {
			disposed = true;
		};
		// Keyed on the reader's LANGUAGE, which a switch moves, so the answer is re-fetched for the
		// catalogue they have just chosen. The VALUE used is read live inside the effect (above), so the
		// dependency is a change signal rather than the input — `setStoredLocale` refreshes the cache the
		// live read consults before this re-runs.
	}, [active, locale, percentOff, attempt]);

	return {rows, settled, spotsLeft, retry};
}
